import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  BookOpen, 
  Users, 
  Wrench, 
  FileText, 
  Calendar,
  Search,
  Upload,
  Download,
  Settings,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Phone,
  Mail,
  Car,
  UserCog,
  ClipboardList,
  Image,
  Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const UserManual = () => {
  const { user } = useAuth();
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Role-based manual content
  const manuals = {
    mechanic: {
      title: "Brukermanual for Mekaniker",
      icon: Wrench,
      color: "text-green-400",
      bgColor: "bg-green-900/30",
      borderColor: "border-green-600",
      sections: [
        {
          id: "dashboard",
          title: "Dashboardet",
          icon: Calendar,
          content: [
            "Dashboardet viser dagens bookinger og statistikk",
            "Bytt mellom Dag, Uke og Måned visning",
            "Klikk på en booking for å se detaljer",
            "Statistikk-kortene viser: Totalt, Venter, Bekreftet, Fullført"
          ]
        },
        {
          id: "new-booking",
          title: "Opprette ny booking",
          icon: ClipboardList,
          content: [
            "Klikk på '+ Ny booking' knappen øverst",
            "Søk etter eksisterende kunde med e-post eller telefon",
            "Eller opprett ny kunde ved å fylle inn informasjon",
            "Velg eller legg til kjøretøy for kunden",
            "Velg dato, tidsluke og tjeneste",
            "Klikk 'Opprett booking' for å fullføre"
          ]
        },
        {
          id: "edit-booking",
          title: "Redigere booking",
          icon: FileText,
          content: [
            "Klikk på en booking i kalenderen",
            "Velg 'Rediger' for å endre detaljer",
            "Oppdater status: Venter → Bekreftet → Fullført",
            "Legg til notater ved behov",
            "Lagre endringene"
          ]
        },
        {
          id: "bulk-edit",
          title: "Masseredigering",
          icon: ClipboardList,
          content: [
            "Gå til Bookinger → Masseredigering",
            "Velg flere bookinger ved å klikke på dem",
            "Eller bruk 'Velg alle' for å markere alle",
            "Endre status på alle valgte samtidig",
            "Tildel mekaniker til flere bookinger",
            "Slett flere bookinger (krever admin-tilgang)"
          ]
        },
        {
          id: "csv-import",
          title: "Importere bookinger",
          icon: Upload,
          content: [
            "Klikk 'Importer CSV' knappen",
            "Last opp en CSV-fil med bookingdata",
            "Påkrevde kolonner: customer_name, customer_email, date, time_slot",
            "Valgfrie kolonner: customer_phone, vehicle_type, notes",
            "Systemet oppretter automatisk kunder og kjøretøy"
          ]
        }
      ]
    },
    company_owner: {
      title: "Brukermanual for Bedriftseier",
      icon: UserCog,
      color: "text-blue-400",
      bgColor: "bg-blue-900/30",
      borderColor: "border-blue-600",
      sections: [
        {
          id: "overview",
          title: "Oversikt",
          icon: Calendar,
          content: [
            "Som bedriftseier har du full tilgang til alle funksjoner",
            "Dashboardet viser sidevisninger og brukeraktivitet",
            "Se alle bookinger, kunder og kjøretøy",
            "Administrer innhold, tjenester og ansatte"
          ]
        },
        {
          id: "crm",
          title: "CRM - Kundeadministrasjon",
          icon: Users,
          content: [
            "Gå til Bookinger → Kunder for å se alle kunder",
            "Søk på navn, e-post, telefon eller firma",
            "Filtrer etter kundetype: Privat, Bedrift, Flåte",
            "Klikk på en kunde for å se full historikk",
            "Se kundens kjøretøy og alle bookinger",
            "Eksporter kundeliste til CSV for backup"
          ]
        },
        {
          id: "vehicles",
          title: "Kjøretøyadministrasjon",
          icon: Car,
          content: [
            "Gå til Bookinger → Kjøretøy for oversikt",
            "Se alle registrerte kjøretøy i systemet",
            "Filtrer etter kjøretøytype",
            "Klikk på kjøretøy for å se eier og servicehistorikk",
            "Importer/eksporter kjøretøy via CSV"
          ]
        },
        {
          id: "bulk-operations",
          title: "Masseoperasjoner",
          icon: ClipboardList,
          content: [
            "Masseredigering: Endre status på flere bookinger",
            "Tildel mekaniker til flere bookinger samtidig",
            "Slett flere bookinger (med bekreftelse)",
            "CSV-import for bookinger, kunder og kjøretøy",
            "CSV-eksport for backup og rapportering"
          ]
        },
        {
          id: "content",
          title: "Innholdsredigering",
          icon: FileText,
          content: [
            "Rediger nettsideinnhold under 'Innhold' menyen",
            "Oppdater hero-seksjonen, tjenester, kontaktinfo",
            "Last opp bilder til mediebiblioteket",
            "Administrer omtaler og partnere",
            "Endre FAQ og chatbot-innstillinger"
          ]
        },
        {
          id: "services",
          title: "Tjenester og priser",
          icon: Wrench,
          content: [
            "Gå til Tjenester for å administrere tilbud",
            "Legg til nye tjenester med beskrivelse",
            "Sett priser og beregnet tid",
            "Aktiver/deaktiver tjenester",
            "Endre rekkefølge på tjenestene"
          ]
        },
        {
          id: "timeslots",
          title: "Tidsluke-innstillinger",
          icon: Settings,
          content: [
            "Gå til Bookinger → Tidsluke-innstillinger",
            "Sett antall plasser per tidsluke",
            "Definer åpningstider per dag",
            "Steng spesifikke datoer (helligdager, ferier)",
            "Sett minimum booking-forvarsel"
          ]
        },
        {
          id: "backup",
          title: "Backup og sikkerhet",
          icon: Download,
          content: [
            "Gå til Media → Backup for sikkerhetskopi",
            "Last ned full backup av databasen",
            "Eksporter kundelister og bookinger",
            "Automatisk backup kan settes opp i produksjon"
          ]
        }
      ]
    },
    content_editor: {
      title: "Brukermanual for Innholdsredaktør",
      icon: FileText,
      color: "text-purple-400",
      bgColor: "bg-purple-900/30",
      borderColor: "border-purple-600",
      sections: [
        {
          id: "cms",
          title: "CMS Innhold",
          icon: FileText,
          content: [
            "Gå til Innhold → CMS Innhold",
            "Rediger tekster for alle seksjoner på nettsiden",
            "Hero: Hovedoverskrift og undertekst",
            "Tjenester: Beskrivelse av tjenesteseksjonen",
            "Kontakt: Adresse, telefon, e-post, åpningstider",
            "Footer: Bunntekst og lenker"
          ]
        },
        {
          id: "services-content",
          title: "Tjenestesider",
          icon: Wrench,
          content: [
            "Gå til Tjenester for å redigere",
            "Hver tjeneste har egen landingsside",
            "Rediger tittel, beskrivelse og pris",
            "Last opp bilder for tjenesten",
            "Sett opp fordeler og inkluderte tjenester"
          ]
        },
        {
          id: "media",
          title: "Mediebibliotek",
          icon: Image,
          content: [
            "Gå til Media → Mediebibliotek",
            "Last opp bilder ved å dra og slippe",
            "Organiser bilder i mapper",
            "Kopier URL til bilder for bruk i innhold",
            "Slett ubrukte bilder for å spare plass"
          ]
        },
        {
          id: "reviews",
          title: "Omtaler",
          icon: Star,
          content: [
            "Gå til Tilbakemelding → Omtaler",
            "Godkjenn nye omtaler fra kunder",
            "Marker beste omtaler som 'featured'",
            "Slett upassende omtaler",
            "Svar på omtaler (vises på nettsiden)"
          ]
        },
        {
          id: "faq",
          title: "FAQ og Chatbot",
          icon: HelpCircle,
          content: [
            "Gå til Innhold → Chatbot",
            "Legg til vanlige spørsmål og svar",
            "Rediger eksisterende FAQ-elementer",
            "Sett opp chatbot-velkomstmelding",
            "Konfigurer automatiske svar"
          ]
        },
        {
          id: "partners",
          title: "Partnere og referanser",
          icon: Users,
          content: [
            "Administrer partnere under Referanser",
            "Legg til logo og beskrivelse",
            "Aktiver/deaktiver partnere",
            "Endre rekkefølge på visning"
          ]
        }
      ]
    },
    master_admin: {
      title: "Brukermanual for Administrator",
      icon: Settings,
      color: "text-red-400",
      bgColor: "bg-red-900/30",
      borderColor: "border-red-600",
      sections: [
        {
          id: "all-access",
          title: "Full tilgang",
          icon: Settings,
          content: [
            "Som master admin har du tilgang til ALT",
            "Alle funksjoner fra bedriftseier + ekstra",
            "Administrer brukere og tilganger",
            "Tilgang til tekniske innstillinger",
            "Se feillogger og systemstatus"
          ]
        },
        {
          id: "user-management",
          title: "Brukeradministrasjon",
          icon: Users,
          content: [
            "Gå til Teknisk → Bulk Admin",
            "Opprett nye brukere med ulike roller",
            "Endre passord for eksisterende brukere",
            "Deaktiver brukere som ikke skal ha tilgang",
            "Tilgjengelige roller: master_admin, company_owner, mechanic, content_editor"
          ]
        },
        {
          id: "technical",
          title: "Tekniske innstillinger",
          icon: Settings,
          content: [
            "Scripts: Legg til egendefinerte scripts",
            "Redirects: Sett opp URL-omdirigeringer",
            "API-tilgang for integrasjoner",
            "Se systemlogger og feil"
          ]
        }
      ]
    }
  };

  // Determine which manual to show based on user role
  const getUserManual = () => {
    if (!user) return null;
    
    switch (user.role) {
      case 'master_admin':
        return manuals.master_admin;
      case 'company_owner':
        return manuals.company_owner;
      case 'mechanic':
        return manuals.mechanic;
      case 'content_editor':
        return manuals.content_editor;
      default:
        return manuals.mechanic;
    }
  };

  const currentManual = getUserManual();

  if (!currentManual) {
    return (
      <div className="text-center py-12">
        <HelpCircle className="w-16 h-16 mx-auto text-gray-600 mb-4" />
        <p className="text-gray-400">Logg inn for å se brukermanualen</p>
      </div>
    );
  }

  const ManualIcon = currentManual.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${currentManual.bgColor} border ${currentManual.borderColor} rounded-lg p-6`}>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${currentManual.bgColor} border ${currentManual.borderColor}`}>
            <ManualIcon className={`w-8 h-8 ${currentManual.color}`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{currentManual.title}</h1>
            <p className="text-gray-400 mt-1">
              Velkommen, {user?.full_name}! Her finner du veiledning for dine oppgaver.
            </p>
          </div>
        </div>
      </div>

      {/* Quick tips */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-medium">Tips!</p>
              <p className="text-gray-400 text-sm">
                Klikk på seksjonene nedenfor for å utvide og lese mer. 
                Du kan alltid komme tilbake hit ved å klikke på "Hjelp" i menyen.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manual sections */}
      <div className="space-y-3">
        {currentManual.sections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections[section.id];
          
          return (
            <Card 
              key={section.id} 
              className="bg-gray-800/50 border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50 transition-colors"
                data-testid={`manual-section-${section.id}`}
              >
                <div className="flex items-center gap-3">
                  <SectionIcon className={`w-5 h-5 ${currentManual.color}`} />
                  <span className="text-white font-medium">{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {isExpanded && (
                <CardContent className="pt-0 pb-4 px-4">
                  <ul className="space-y-2 ml-8">
                    {section.content.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Contact support */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Trenger du hjelp?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-gray-400">
            Hvis du har spørsmål eller trenger assistanse, ta kontakt:
          </p>
          <div className="flex flex-wrap gap-4">
            <a 
              href="mailto:nettpilot@nettpilot.no" 
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
            >
              <Mail className="w-4 h-4" />
              nettpilot@nettpilot.no
            </a>
            <a 
              href="tel:+4740043101" 
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
            >
              <Phone className="w-4 h-4" />
              +47 400 43 101
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Show all manuals for master admin */}
      {user?.role === 'master_admin' && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Alle brukermanualer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400 mb-4">
              Som administrator kan du se alle brukermanualer:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(manuals).filter(([key]) => key !== 'master_admin').map(([key, manual]) => {
                const Icon = manual.icon;
                return (
                  <div 
                    key={key}
                    className={`p-4 rounded-lg ${manual.bgColor} border ${manual.borderColor}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-5 h-5 ${manual.color}`} />
                      <span className="text-white font-medium text-sm">
                        {key === 'mechanic' && 'Mekaniker'}
                        {key === 'company_owner' && 'Bedriftseier'}
                        {key === 'content_editor' && 'Innholdsredaktør'}
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs">
                      {manual.sections.length} seksjoner
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserManual;
